/**
 * Formateo específico de la web. Todo lo que ya resuelve el paquete de tokens
 * (moneda, porcentajes, duraciones, KPI) se reexporta tal cual para que nadie
 * tenga la tentación de escribir su propia versión.
 */

import { format, formatDistanceToNowStrict, isToday, isYesterday, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

export {
  formatCurrency,
  formatDurationHours,
  formatDurationMinutes,
  formatKpi,
  formatNumber,
  formatPct,
  deltaTone,
  evaluateKpi,
  initials,
  avatarTone,
  labelFromEnum,
  iconFromEnum,
  toneFromEnum,
  defaultKpiTargets,
} from '@crm/design-tokens';

/** Convierte lo que llegue de la API a `Date`, o `null` si no hay valor. */
export function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = typeof value === 'string' ? parseISO(value) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** `24 ago 2026` */
export function formatDate(value: string | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, "d MMM yyyy", { locale: es }) : '—';
}

/** `24/08/2026 14:30` — para columnas de tabla donde importa la precisión. */
export function formatDateTime(value: string | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'dd/MM/yyyy HH:mm', { locale: es }) : '—';
}

/** `14:30` */
export function formatTime(value: string | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'HH:mm', { locale: es }) : '—';
}

/**
 * Fecha «humana» para bitácoras y chats: hoy y ayer se nombran, el resto se
 * fecha. Es lo que la gente espera de una conversación.
 */
export function formatSmartDate(value: string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  if (isToday(date)) return `Hoy ${format(date, 'HH:mm')}`;
  if (isYesterday(date)) return `Ayer ${format(date, 'HH:mm')}`;
  return format(date, "d MMM yyyy, HH:mm", { locale: es });
}

/** `hace 3 h` — sin el prefijo «alrededor de», que alarga sin aportar. */
export function formatRelative(value: string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return `hace ${formatDistanceToNowStrict(date, { locale: es })}`;
}

/** Encabezado de día para agrupar mensajes: `jueves, 24 de agosto`. */
export function formatDayHeading(value: string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  if (isToday(date)) return 'Hoy';
  if (isYesterday(date)) return 'Ayer';
  return format(date, "EEEE, d 'de' MMMM", { locale: es });
}

/** Fecha en el formato `YYYY-MM-DD` que espera el escalar `Date` del contrato. */
export function toIsoDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** Instante RFC 3339 para el escalar `DateTime`. */
export function toIsoDateTime(date: Date): string {
  return date.toISOString();
}

/**
 * Los importes viajan como cadena (`Decimal`) a propósito, para no perder
 * precisión en el JSON. Se convierten a número sólo al pintarlos o graficarlos.
 */
export function decimalToNumber(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/** `1,2 MB` — tamaño de adjuntos. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toLocaleString('es-PE', { maximumFractionDigits: 1 })} ${units[unit]}`;
}

/** Teléfono peruano legible: `+51 987 654 321`. */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return '—';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 9) return digits.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
  if (digits.length === 11 && digits.startsWith('51')) {
    return `+51 ${digits.slice(2).replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`;
  }
  return value;
}

/** Recorta preservando palabras, para vistas previas de mensajes. */
export function truncate(text: string | null | undefined, max = 80): string {
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}…`;
}
